import { useState } from 'react';
import { PlanCard } from './PlanCard';
import { usePlans, type SubscriptionPlan, type BillingPeriod } from '@/hooks/useSubscription';
import { Loader2 } from 'lucide-react';

interface PlanSelectorProps {
  currentPlanId?: string | null;
  currentPlanType?: SubscriptionPlan | null;
  onSelectPlan: (planId: string, planType: SubscriptionPlan, billingPeriod: BillingPeriod) => void;
  loading?: boolean;
  loadingPlanId?: string | null;
  defaultBillingPeriod?: BillingPeriod;
}

export function PlanSelector({
  currentPlanId,
  currentPlanType,
  onSelectPlan,
  loading,
  loadingPlanId,
  defaultBillingPeriod = 'monthly',
}: PlanSelectorProps) {
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>(defaultBillingPeriod);
  const { plans, isLoading: isPlansLoading, error: plansError } = usePlans();

  // Filter plans by billing period and sort by tier_rank
  const filteredPlans = plans
    ?.filter((plan) => plan.billing_period === billingPeriod && plan.is_active)
    .sort((a, b) => a.tier_rank - b.tier_rank) || [];

  // Determine which plan should be marked as recommended (Pro is tier_rank 2)
  const proTierRank = 2;

  if (isPlansLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (plansError) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-center">
        <p className="text-destructive">Failed to load plans. Please try again.</p>
      </div>
    );
  }

  if (filteredPlans.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-4 text-center">
        <p className="text-muted-foreground">No plans available.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Billing period toggle */}
      <div className="mb-8 flex items-center justify-center gap-4">
        <button
          onClick={() => setBillingPeriod('monthly')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            billingPeriod === 'monthly'
              ? 'text-sapling-primary-100'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Monthly
        </button>
        <div className="relative">
          <button
            onClick={() => setBillingPeriod('yearly')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              billingPeriod === 'yearly'
                ? 'text-sapling-primary-100'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Yearly
          </button>
          <span className="absolute -right-12 -top-2 rounded-full bg-sapling-primary-100 px-2 py-0.5 text-xs text-white">
            Save ~8%
          </span>
        </div>
      </div>

      {/* Plan cards */}
      <div className="grid gap-6 md:grid-cols-3">
        {filteredPlans.map((plan) => {
          // Extract features from the features JSONB
          const features = Array.isArray(plan.features?.feature_list)
            ? plan.features.feature_list as string[]
            : getDefaultFeatures(plan.plan_type);

          const isCurrentPlan = currentPlanId === plan.id ||
            (currentPlanType === plan.plan_type && !currentPlanId);

          return (
            <PlanCard
              key={plan.id}
              id={plan.id}
              name={plan.plan_name}
              price={plan.price}
              period={plan.billing_period}
              features={features}
              recommended={plan.tier_rank === proTierRank}
              currentPlan={isCurrentPlan}
              onSelect={() => onSelectPlan(plan.id, plan.plan_type, plan.billing_period)}
              loading={loading && loadingPlanId === plan.id}
              disabled={loading}
            />
          );
        })}
      </div>
    </div>
  );
}

// Fallback features if not in database
function getDefaultFeatures(planType: SubscriptionPlan): string[] {
  switch (planType) {
    case 'solo':
      return [
        'Personal Finance Tracking',
        '1 Business',
        'PDF Statement Import',
        'Auto-Categorization',
        'All Financial Reports',
      ];
    case 'pro':
      return [
        'Up to 5 Businesses',
        'Everything in Solo',
        '50 Categorization Rules',
        'All Financial Reports',
        'CPA-Ready Tax Exports',
      ];
    case 'team':
      return [
        'Unlimited Businesses',
        'Everything in Pro',
        'Unlimited Team Members',
        'Role-Based Permissions',
        'Priority Support',
      ];
    default:
      return [];
  }
}
